import { Textarea, cn } from '@newfront-insurance/core-ui/v2';
import { useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export type SkillContentEditorTab = 'write' | 'preview';

export function SkillContentEditor({
  id,
  value,
  onChange,
  onBlur,
  disabled,
  placeholder,
  className,
  textareaClassName,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  textareaClassName?: string;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [activeTab, setActiveTab] = useState<SkillContentEditorTab>('write');

  useEffect(() => {
    if (!textareaRef.current || activeTab !== 'write') {
      return;
    }

    textareaRef.current.style.height = 'auto';
    textareaRef.current.style.height = `${Math.max(textareaRef.current.scrollHeight + 2, 210)}px`;
  }, [activeTab, value]);

  return (
    <div className={cn('overflow-hidden rounded-[4px] border border-[#BEC7D0] bg-white', className)}>
      <div className="flex h-10 items-center gap-4 border-b border-[#DEE2E7] bg-[#FAFBFC] px-4">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'write'}
          className={cn(
            "font-['Open_Sans',sans-serif] text-[14px] leading-5 font-semibold",
            activeTab === 'write' ? 'text-[#262D46]' : 'text-[#257FFB]',
          )}
          onClick={() => setActiveTab('write')}
        >
          Write
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'preview'}
          className={cn(
            "font-['Open_Sans',sans-serif] text-[14px] leading-5 font-semibold",
            activeTab === 'preview' ? 'text-[#262D46]' : 'text-[#257FFB]',
          )}
          onClick={() => setActiveTab('preview')}
        >
          Preview
        </button>
      </div>

      {activeTab === 'write' ? (
        <Textarea
          ref={textareaRef}
          id={id}
          aria-label="Skill content"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "min-h-[210px] resize-none rounded-none border-0 px-4 py-4 font-['Open_Sans',sans-serif]",
            'text-[14px] leading-5 text-[#262D46] placeholder:text-[#98A6B5] focus-visible:ring-0',
            textareaClassName,
          )}
        />
      ) : (
        <div
          role="tabpanel"
          aria-label="Skill content preview"
          className="prose prose-sm prose-a:text-brand-400 min-h-[210px] max-w-none px-4 py-4 break-words text-[#262D46] [&>*:first-child]:mt-0"
        >
          <Markdown remarkPlugins={[remarkGfm]} skipHtml>
            {value}
          </Markdown>
        </div>
      )}
    </div>
  );
}
